import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosRun } from '../utils/axiosWrap';
import { config } from '../config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';

// Стилі для документів
const DocumentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  border: '1px solid #e0e0e0',
  borderRadius: '1px',
  boxShadow: theme.shadows[3],
}));

const ProfileDisplayApplicant = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  // Завантаження даних
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const applicantItem = await axiosRun(config.apiLinks.applicantItem.replace("%id%", id), 'GET');
        setData(applicantItem);
        setStatus(applicantItem.item.status);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Скачати архів
  const handleDownload = async (id, fileName) => {

    setDownloadLoading(true);
    try {
      const response = await axiosRun(
        config.apiLinks.downloadApplicantDocuments,
        'PUT',
        {id:id},
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}--documents-applicant-${id}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Помилка завантаження:', err);
      alert('Не вдалося завантажити архів');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleStatusChange = (event) => setStatus(event.target.value);

  const handleSaveStatus = async () => {
    setLoading(true);
    try {
      await axiosRun(config.apiLinks.updateApplicantStatus.replace("%id%", id), 'PUT', { status });
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Помилка збереження статусу:', err);
      setError('Не вдалося зберегти статус');
    }
    setLoading(false);
  };

  const handleSnackbarClose = () => setOpenSnackbar(false);

  // Блок скачування (окрема функція)
  const renderDownloadBlock = (currentData, fileName) => (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mt: 3,
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            📥 Документ доступний для завантаження
          </Typography>
          <Typography variant="body2" color="text.secondary">
            У захищеному ZIP-архіві
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={e => {handleDownload(currentData.id, fileName)}}
          disabled={downloadLoading}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            px: 4,
            py: 1.2,
            fontWeight: 'bold'
          }}
        >
          {downloadLoading ? 'Завантаження...' : 'Завантажити ZIP'}
        </Button>
      </Box>

      {currentData.zipPassword && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #cbd5e1' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Пароль до архіву:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              bgcolor: '#fff',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid #e2e8f0',
              display: 'inline-block',
              letterSpacing: '2px'
            }}
          >
            {currentData.zipPassword}
          </Typography>
        </Box>
      )}
    </Paper>
  );

  // Рендер одного документа
  const renderDocument = (doc, docType, index, metaData) => {
    {console.log('metaData:', metaData)}


    if (docType === 'taxpayer-card' ) {
      // Можна уточнити точний docType пізніше
      const downloadFileName = `Особисті-дані--${doc.lastNameUA} ${doc.firstNameUA}`;

      return (
        <DocumentPaper key={`${docType}-${index}`}>
          <Typography variant="h5" gutterBottom>
            Особисті дані / ID-карта
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography><strong>Дата створення:</strong> {doc.creationDate}</Typography>
          <Typography><strong>Номер документа:</strong> {doc.docNumber}</Typography>

          <Typography><strong>ПІБ (UA):</strong> {doc.lastNameUA} {doc.firstNameUA} {doc.middleNameUA}</Typography>

          <Typography><strong>Дата народження:</strong> {doc.birthday}</Typography>

          {/* Додаткові поля, якщо вони з'являться в майбутньому */}
          {doc.genderUA && <Typography><strong>Стать:</strong> {doc.genderUA}</Typography>}
          {doc.birthPlace && <Typography><strong>Місце народження:</strong> {doc.birthPlace}</Typography>}

          <Divider sx={{ my: 2 }} />

          {/* Мета-дані (однакові для всіх документів) */}
          <Typography><strong>ID документа:</strong> {metaData.id}</Typography>
          <Typography><strong>Статус відправки:</strong> {metaData.isSend}</Typography>
          <Typography><strong>Отримано:</strong> {metaData.isDone ? 'Так' : 'Ні'}</Typography>
          <Typography><strong>Створено:</strong> {new Date(metaData.createdAt).toLocaleString()}</Typography>
          <Typography><strong>Оновлено:</strong> {new Date(metaData.updatedAt).toLocaleString()}</Typography>
          {renderDownloadBlock(metaData, downloadFileName)}
        </DocumentPaper>
      );
    }

    if (docType === 'reference-internally-displaced-person') {
      const downloadFileName = `${doc.docType}--${doc.lastName} ${doc.firstName}`;

      return (
        <DocumentPaper key={`${docType}-${index}`}>
          <Typography variant="h5" gutterBottom>Довідка ВПО</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography><strong>Тип документа:</strong> {doc.docType}</Typography>
          <Typography><strong>Номер документа:</strong> {doc.docNumber}</Typography>
          <Typography><strong>ПІБ:</strong> {doc.lastName} {doc.firstName} {doc.middleName}</Typography>
          <Typography><strong>Дата видачі:</strong> {doc.issueDate}</Typography>
          <Typography><strong>Дата народження:</strong> {doc.birthDate}</Typography>
          <Typography><strong>Стать:</strong> {doc.gender}</Typography>
          <Typography><strong>Орган видачі:</strong> {doc.department}</Typography>
          <Typography><strong>Номер документа особи:</strong> {doc.docIdentity.number}</Typography>
          <Typography><strong>Дата видачі документа особи:</strong> {doc.docIdentity.issueDate}</Typography>
          <Typography><strong>Орган видачі документа особи:</strong> {doc.docIdentity.department}</Typography>
          <Typography><strong>Місце народження:</strong> {doc.address.birth}</Typography>
          <Typography><strong>Адреса реєстрації:</strong> {doc.address.registration}</Typography>
          <Typography><strong>Фактична адреса:</strong> {doc.address.actual}</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography><strong>ID документа:</strong> {metaData.id}</Typography>
          <Typography><strong>Статус відправки:</strong> {metaData.isSend}</Typography>
          <Typography><strong>Отримано:</strong> {metaData.isDone ? 'Так' : 'Ні'}</Typography>
          <Typography><strong>Дійсний до:</strong> {new Date(metaData.expiredTo).toLocaleString()}</Typography>
          <Typography><strong>Створено:</strong> {new Date(metaData.createdAt).toLocaleString()}</Typography>
          <Typography><strong>Оновлено(отримано):</strong> {new Date(metaData.updatedAt).toLocaleString()}</Typography>

          {/* Блок скачування */}
          {renderDownloadBlock(metaData, downloadFileName)}
        </DocumentPaper>
      );
    }
    const downloadFileNamePassport = `${'internal-passport' ? 'Внутрішній паспорт' : 'Закордонний паспорт'}--${doc.lastNameUA} ${doc.firstNameUA}`;
    // Паспорти
    return (
      <DocumentPaper key={`${docType}-${index}`}>
        <Typography variant="h5" gutterBottom>
          {docType === 'internal-passport' ? 'Внутрішній паспорт' : 'Закордонний паспорт'}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography><strong>Номер документа:</strong> {doc.docNumber}</Typography>
        <Typography><strong>ПІБ (UA):</strong> {doc.lastNameUA} {doc.firstNameUA} {doc.middleNameUA}</Typography>
        <Typography><strong>ПІБ (EN):</strong> {doc.lastNameEN} {doc.firstNameEN}</Typography>
        <Typography><strong>Дата народження:</strong> {doc.birthday}</Typography>
        <Typography><strong>Місце народження:</strong> {doc.birthPlaceUA}</Typography>
        <Typography><strong>Стать:</strong> {doc.genderUA} ({doc.genderEN})</Typography>
        <Typography><strong>Національність:</strong> {doc.nationalityUA}</Typography>
        <Typography><strong>Дата видачі:</strong> {doc.issueDate}</Typography>
        <Typography><strong>Дата закінчення:</strong> {doc.expirationDate}</Typography>
        <Typography><strong>Номер запису:</strong> {doc.recordNumber}</Typography>
        <Typography><strong>Орган видачі:</strong> {doc.department || doc.departmentUA}</Typography>
        {doc.countryCode && <Typography><strong>Код країни:</strong> {doc.countryCode}</Typography>}
        <Typography><strong>Номер платника податків:</strong> {doc.taxpayerNumber}</Typography>
        <Typography><strong>Адреса проживання:</strong> {doc.residenceUA}</Typography>
        <Divider sx={{ my: 2 }} />
        <Typography><strong>ID документа:</strong> {metaData.id}</Typography>
        <Typography><strong>Статус відправки:</strong> {metaData.isSend}</Typography>
        <Typography><strong>Отримано:</strong> {metaData.isDone ? 'Так' : 'Ні'}</Typography>
        <Typography><strong>Дійсний до:</strong> {new Date(metaData.expiredTo).toLocaleString()}</Typography>
        <Typography><strong>Створено запис:</strong> {new Date(metaData.createdAt).toLocaleString()}</Typography>
        <Typography><strong>Оновлено запис (отримано):</strong> {new Date(metaData.updatedAt).toLocaleString()}</Typography>

        {/* Блок скачування */}
        {renderDownloadBlock(metaData, downloadFileNamePassport)}
      </DocumentPaper>
    );
  };
  console.log('parsedDocs:', data?.itemsDocs);
  const parsedDocs = data?.itemsDocs?.map((docItem) => {
    if (!docItem.raw) return { ...docItem, parsedRaw: null };
    try {
      return { ...docItem, parsedRaw: JSON.parse(docItem.raw) };
    } catch (error) {
      console.error(`Помилка парсингу raw для документа ${docItem.id}:`, error);
      return { ...docItem, parsedRaw: null };
    }
  }) || [];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, p: 2 }}><Typography color="error">Помилка: {error}</Typography></Box>;
  if (!data) return <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, p: 2 }}><Typography>Дані відсутні</Typography></Box>;

  return (
    <Paper>
      <Box sx={{ maxWidth: 1200, mx: 'auto', my: 4, p: 2, ml: '3%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

        {/* Навігація + статус */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
          <Button variant="contained" color="primary" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 'bold' }}>
            Назад
          </Button>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="status-select-label">Статус</InputLabel>
            <Select labelId="status-select-label" value={status} label="Статус" onChange={handleStatusChange}>
              <MenuItem value="true">Опрацьовано</MenuItem>
              <MenuItem value="false">Неопрацьовано</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={handleSaveStatus} sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 'bold' }}>
            Зберегти
          </Button>
        </Box>

        {/* Інформація про особу */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: '8px', width: '100%' }}>
          <Typography variant="h3" gutterBottom>Запит абітурієнта № {data.item.id}</Typography>
          <Typography variant="h5" gutterBottom>Інформація про особу</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography><strong>ПІБ (UA):</strong> {data.item.lastName} {data.item.firstName} {data.item.middleName}</Typography>
          <Typography><strong>ПІБ (EN):</strong> {data.item.lastNameEn} {data.item.firstNameEn} {data.item.middleNameEn || '-'}</Typography>
          <Typography><strong>ІПН:</strong> {data.item.ipnNumber}</Typography>
          <Typography><strong>Дата народження:</strong> {new Date(data.item.birthday).toLocaleDateString()}</Typography>
          <Typography><strong>Стать:</strong> {data.item.gender}</Typography>
          <Typography><strong>Номер паспорта:</strong> {data.item.passportNumber}</Typography>
          <Typography><strong>Email:</strong> {data.item.email || '-'}</Typography>
          <Typography><strong>Phone:</strong> {data.item.contactPhoneRaw || '-'}</Typography>
          <Typography><strong>Створено запит:</strong> {new Date(data.item.createdAt).toLocaleString()}</Typography>
          <Typography><strong>Оновлено запит:</strong> {new Date(data.item.updatedAt).toLocaleString()}</Typography>
          {data.item.ApplicantToStudyPlaces?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h5" gutterBottom>Подані місця навчання</Typography>
              {data.item.ApplicantToStudyPlaces.map((place, index) => (
                <Box key={index} sx={{ mt: 1, pl: 2 }}>
                  <Typography><strong>Факультет:</strong> {place.Faculty.name}</Typography>
                  <Typography><strong>Спеціальність:</strong> {place.Speciality.name}</Typography>
                  <Typography><strong>Спеціалізація:</strong> {place.Specializations?.name}</Typography>
                  {place.rate && <Typography><strong>Рейтинг:</strong> {place.rate}</Typography>}
                  <Typography><strong>Дата створення:</strong> {new Date(place.createdAt).toLocaleString()}</Typography>
                  <Typography><strong>Дата оновлення:</strong> {new Date(place.updatedAt).toLocaleString()}</Typography>
                  {index < data.item.ApplicantToStudyPlaces.length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </Box>
              ))}
            </>
          )}
        </Paper>

        {/* Документи */}
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: '8px', width: '100%' }}>
          <Typography variant="h4" gutterBottom>Надані документи</Typography>
          <Divider sx={{ mb: 2 }} />

          {parsedDocs.map((docItem, index) => (
            <Box key={index}>
              {docItem.parsedRaw ? (
                <>
                  {docItem.parsedRaw['taxpayer-card']?.map((doc, idx) => renderDocument(doc, 'taxpayer-card', idx, docItem))}
                  {docItem.parsedRaw['internal-passport']?.map((doc, idx) => renderDocument(doc, 'internal-passport', idx, docItem))}
                  {docItem.parsedRaw['foreign-passport']?.map((doc, idx) => renderDocument(doc, 'foreign-passport', idx, docItem))}
                  {docItem.parsedRaw['reference-internally-displaced-person']?.map((doc, idx) => renderDocument(doc, 'reference-internally-displaced-person', idx, docItem))}
                </>
              ) : (
                <DocumentPaper key={`empty-${docItem.id}`}>
                  <Typography variant="h6" gutterBottom>Документ (ID: {docItem.id})</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography color="text.secondary">Дані документа відсутні (raw: null)</Typography>
                  <Typography><strong>Статус відправки:</strong> {docItem.isSend}</Typography>
                  <Typography><strong>Отримано:</strong> {docItem.isDone ? 'Так' : 'Ні'}</Typography>
                  <Typography><strong>Дійсний до:</strong> {new Date(docItem.expiredTo).toLocaleString()}</Typography>
                  <Typography><strong>Створено запис:</strong> {new Date(docItem.createdAt).toLocaleString()}</Typography>
                  <Typography><strong>Оновлено запис(отримано):</strong> {new Date(docItem.updatedAt).toLocaleString()}</Typography>
                </DocumentPaper>
              )}
            </Box>
          ))}
        </Paper>

        <Snackbar open={openSnackbar} autoHideDuration={1000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity="success" onClose={handleSnackbarClose} sx={{ width: '100%' }}>Збережено</Alert>
        </Snackbar>
      </Box>
    </Paper>
  );
};

export default ProfileDisplayApplicant;